import { FormikProps } from 'formik'
import React from 'react'
import styled from 'styled-components'
import { Button } from 'sutra/components/Button'
import { FormItem } from 'sutra/components/Form'
import { Select } from 'sutra/components/Select/Select'

import { CustomFields } from 'features/project-form/components'
import { FormTitle } from 'features/project-form/components/FormTitle'
import { SourceProvider } from 'features/project-form/features/source/components/SourceProviders'
import { SourceValues } from 'features/project-form/features/source/SourceForm'
import { ProductForSource } from 'features/project-form/features/source/types'
import { UploadType } from 'features/project-form/utils/enums'
import {
  sourceTypes,
  useSourceTypes,
} from 'features/project-form/utils/source-types'

export type FormikType = FormikProps<SourceValues>
type Props = {
  formik: FormikType
  products: ProductForSource[]
  onDelete?: () => void
}

const fallbackOptions = [UploadType.File, ...Object.keys(sourceTypes)]

export const NewSource: React.FC<Props> = ({ formik, onDelete, products }) => {
  const { data: catalogSourceTypes } = useSourceTypes()
  const options = catalogSourceTypes
    ? [UploadType.File, ...Object.keys(catalogSourceTypes)]
    : fallbackOptions

  return (
    <Wrapper>
      <FormTitle>New Source</FormTitle>
      <FormItem
        label="Source Type"
        wrapperCol={{ span: 6 }}
        validateStatus={formik.errors.sourceType && 'error'}
        help={formik.errors.sourceType}
      >
        <Select
          placeholder="Source Type"
          searchable
          value={formik.values.sourceType || undefined}
          onChange={(option) => {
            formik.setFieldValue('sourceType', option)
            formik.setFieldValue('sourceUrl', '')
          }}
          allowClear
          options={options.map((option) => ({ value: option, label: option }))}
        />
      </FormItem>

      <FormItem
        label="Product"
        wrapperCol={{ span: 6 }}
        validateStatus={formik.errors.product_id && 'error'}
        help={formik.errors.product_id}
      >
        <Select
          placeholder="Product"
          defaultValue={formik.values.product_id || undefined}
          value={formik.values.product_id}
          onChange={(option) => formik.setFieldValue('product_id', option)}
          allowClear
          options={products.map((product) => ({
            value: product.id,
            label: product.name,
          }))}
        />
      </FormItem>

      <SourceProvider formik={formik} sourceType={formik.values.sourceType} />
      <Error>{formik.errors.custom}</Error>

      {!!formik.values.sourceType &&
        formik.values.sourceType !== UploadType.File && (
          <CustomFields
            fields={formik.values.custom}
            onFieldAdd={(field: string, value: string) =>
              formik.setFieldValue(`custom`, {
                ...formik.values.custom,
                [field]: value,
              })
            }
            onFieldRemove={(field: string) => {
              const values = formik.values.custom
              if (values) {
                delete values[field]
              }
              formik.setFieldValue(`custom`, values)
            }}
          />
        )}

      <ButtonsWrapper>
        {onDelete && (
          <SubmitButton variant="secondary" onClick={onDelete}>
            Delete
          </SubmitButton>
        )}
        <SubmitButton variant="primary" type="submit">
          {onDelete ? 'Save' : 'Add Source'}
        </SubmitButton>
      </ButtonsWrapper>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  width: 800px;
  height: 700px;
  overflow: auto;
  display: flex;
  flex-direction: column;

  @media screen and (max-width: 1024px) {
    width: auto;
  }
`
const ButtonsWrapper = styled.div`
  margin-top: auto;
  margin-bottom: 0;
  display: flex;
  justify-content: center;
`
const SubmitButton = styled(Button)`
  margin: 40px 20px 0;
`
const Error = styled.div`
  text-align: center;
  color: #ff4d4f;
`
